import { Draft, TKeysDefine } from '@apia/dom-store';
import { TListboxState, makeRowKey } from './common';
import { addBoundary, getIndex } from '@apia/util';

type TListboxOperation<Payload> = (
  state: Draft<TListboxState>,
  payload: Payload,
  keys: TKeysDefine,
) => void;

const filter: TListboxOperation<{
  searchString: string;
}> = makeOperation((state, { searchString }, keys) => {
  let focusChanged = false;
  const rows = new Array(state.rowsCount).fill(1).map((_, i) => i);

  const shownRows: number[] = [];
  const hiddenRows = rows.filter((_, i) => {
    const el = document.getElementById(`${state.id}__row__${i}`);
    if (!el) {
      console.warn('Element not found', `${state.id}__row__${i}`);
      return false;
    }

    const matches = state.searchFunction(el.ariaLabel ?? '', searchString);

    if (matches) {
      el.style.display = 'block';
    } else {
      el.style.display = 'none';
    }

    if (matches) shownRows.push(i);
    else if (state.focusedRow === i) {
      focusChanged = true;
    }

    return !matches;
  });
  keys.set(hiddenRows.map((current) => makeRowKey(current)));
  state.hiddenRows = hiddenRows;
  state.shownRows = shownRows;

  if (focusChanged) {
    for (const row of rows) {
      if (!hiddenRows.includes(row)) {
        state.focusedRow = row;
        keys.push(makeRowKey(row));
        keys.push(makeRowKey(state.focusedRow));
        break;
      }
    }
  }
});

function makeOperation<T>(operation: TListboxOperation<T>) {
  const method: TListboxOperation<T> = (state, payload, keys) => {
    keys.push('searchbox');
    keys.push('listbox');
    operation(state, payload, keys);
  };
  return method;
}

const moveFocusAndToggleSelection: TListboxOperation<{
  isShiftKey: boolean;
  which: 'next' | 'previous' | 'first' | 'last' | 'pageUp' | 'pageDown';
}> = makeOperation((state, { isShiftKey, which }, keys) => {
  if (state.shownRows.length <= 1) {
    keys.push(makeRowKey(state.focusedRow));
    state.focusedRow = state.shownRows[0];

    if (state.shownRows.length === 1) keys.push(makeRowKey(state.focusedRow));

    return;
  }

  switch (which) {
    case 'first':
      {
        const newFocused = state.shownRows[0];
        if (newFocused === state.focusedRow) return;

        keys.push(makeRowKey(state.focusedRow));
        keys.push(makeRowKey(newFocused));

        if (state.isMultipleSelection) {
          if (isShiftKey) {
            const selectedElements = new Array(state.focusedRow + 1)
              .fill(1)
              .map((_, i) => i);
            keys.set(selectedElements.map((_, i) => makeRowKey(i)));
            state.selectedRows = selectedElements;
            state.lastSelectedRow = newFocused;
          }
        } else {
          state.selectedRows = [newFocused];
          state.lastSelectedRow = newFocused;
        }

        state.focusedRow = newFocused;
      }
      break;
    case 'last':
      {
        const newFocused = state.shownRows[state.shownRows.length - 1];
        if (newFocused === state.focusedRow) return;

        keys.push(makeRowKey(state.focusedRow));
        keys.push(makeRowKey(newFocused));

        if (state.isMultipleSelection) {
          if (isShiftKey) {
            const selectedElements = new Array(
              state.rowsCount - state.focusedRow,
            )
              .fill(1)
              .map((_, i) => i + Math.min(newFocused, state.focusedRow));
            keys.push(...selectedElements.map((i) => makeRowKey(i)));
            state.selectedRows = selectedElements;
            state.lastSelectedRow = newFocused;
          }
        } else {
          state.selectedRows = [newFocused];
          state.lastSelectedRow = newFocused;
        }

        state.focusedRow = newFocused;
      }
      break;
    case 'previous':
    case 'next':
    case 'pageDown':
    case 'pageUp':
      {
        const sum = getIndex(
          [-1, 1, -10, 10],
          [
            which === 'previous',
            which === 'next',
            which === 'pageUp',
            which === 'pageDown',
          ],
        );

        const currentIndex = state.shownRows.findIndex(
          (current) => state.focusedRow === current,
        );
        const newFocused =
          state.shownRows[
            addBoundary({
              number: currentIndex + sum,
              min: 0,
              max: state.shownRows.length - 1,
            })
          ];
        if (newFocused === state.focusedRow) return;

        keys.push(makeRowKey(state.focusedRow));
        keys.push(makeRowKey(newFocused));

        if (state.isMultipleSelection) {
          if (isShiftKey) {
            if (state.selectedRows.includes(newFocused)) {
              state.selectedRows = state.selectedRows.filter(
                (current) => current !== newFocused,
              );
            } else {
              state.selectedRows.push(newFocused);
            }
            state.lastSelectedRow = newFocused;
          }
        } else {
          state.selectedRows = [newFocused];
          state.lastSelectedRow = newFocused;
        }

        state.focusedRow = newFocused;
      }
      break;
  }
  state.isEverythingSelected = false;
});

const resetFocus: TListboxOperation<null> = makeOperation((state, _, keys) => {
  keys.push(makeRowKey(state.focusedRow));
  state.focusedRow = 0;
  keys.push(makeRowKey(0));
});

const searchElementsByStartingCharacters: TListboxOperation<{
  characters: string;
}> = makeOperation((state, payload, keys) => {
  const currentIndex = state.shownRows.findIndex(
    (current) => current === state.focusedRow,
  );

  function match(rowIndex: number) {
    keys.push(makeRowKey(state.focusedRow));

    state.focusedRow = rowIndex;

    if (!state.isMultipleSelection) {
      keys.push(...state.selectedRows.map((current) => makeRowKey(current)));
      state.selectedRows = [rowIndex];
    }

    keys.push(makeRowKey(state.focusedRow));
  }

  function tryIndex(rowIndex: number) {
    const el = document.getElementById(`${state.id}__row__${rowIndex}`);
    if (!el) {
      console.warn('Element not found', `${state.id}__row__${rowIndex}`);
      return false;
    }

    const matches = state.startsWithFunction(
      el.ariaLabel ?? '',
      payload.characters,
    );

    if (matches) {
      match(state.shownRows[rowIndex]);
      return true;
    }

    return false;
  }

  for (let i = currentIndex + 1; i < state.shownRows.length; i++) {
    if (tryIndex(i)) return;
  }

  for (let i = 0; i < currentIndex; i++) {
    if (tryIndex(i)) return;
  }
});

/**
 * No actualiza ningún key porque al llamar a selectAll, todos los elementos
 * son afectados y el comportamiento por defecto del store es actualizar todo
 */
const selectAll: TListboxOperation<void> = makeOperation(
  (state, payload, keys) => {
    keys.push(...state.selectedRows.map(makeRowKey));
    if (state.isEverythingSelected) {
      state.isEverythingSelected = false;
      state.selectedRows = [];
    } else {
      state.isEverythingSelected = true;
      state.selectedRows = state.shownRows;
    }
    state.lastSelectedRow = state.focusedRow;
    keys.push(...state.shownRows.map(makeRowKey));
  },
);

const setState: TListboxOperation<
  Partial<TListboxState> & { affectedKeys: string[] }
> = makeOperation((state, { affectedKeys, ...payload }, keys) => {
  const newRowsCount = payload.rowsCount ?? state.rowsCount;
  if (state.rowsCount < newRowsCount) {
    for (let i = state.rowsCount; i < (payload.rowsCount as number); i++) {
      state.shownRows.push(i);
    }
  } else if (state.rowsCount > newRowsCount) {
    state.shownRows = state.shownRows.filter(
      (current) => current < newRowsCount,
    );
    if (state.focusedRow > newRowsCount - 1) {
      state.focusedRow = newRowsCount - 1;
      keys.push(makeRowKey(state.focusedRow));
    }
    state.selectedRows = state.selectedRows.filter(
      (current) => current < newRowsCount,
    );
  }

  Object.assign(state, payload);
  keys.set(affectedKeys);
  state.isEverythingSelected = false;
});

const updateFocusByClickEvent: TListboxOperation<{
  rowIndex: number;
  isCtrlKey: boolean;
  isShiftKey: boolean;
}> = makeOperation((state, payload, keys) => {
  keys.push(makeRowKey(state.focusedRow));
  keys.push(makeRowKey(payload.rowIndex));

  state.focusedRow = payload.rowIndex;
  if (payload.isShiftKey) {
    updateFocusBySpaceEvent(state, { isShiftKey: payload.isShiftKey }, keys);
  } else {
    state.lastSelectedRow = payload.rowIndex;
    if (payload.isCtrlKey) {
      if (state.selectedRows.includes(payload.rowIndex)) {
        state.selectedRows = state.selectedRows.filter(
          (current) => current !== payload.rowIndex,
        );
      } else {
        state.selectedRows.push(payload.rowIndex);
      }
    } else {
      keys.push(...state.selectedRows.map((current) => makeRowKey(current)));
      state.selectedRows = [state.focusedRow];
    }
  }
});

const updateFocusBySpaceEvent: TListboxOperation<{
  isShiftKey: boolean;
}> = makeOperation((state, { isShiftKey }, keys) => {
  const rowIndex = state.focusedRow;
  keys.push(makeRowKey(rowIndex));
  if (state.isMultipleSelection) {
    if (isShiftKey) {
      if (state.lastSelectedRow === -1) {
        state.selectedRows.push(rowIndex);
      } else {
        const newSelection = new Array(
          Math.abs(state.lastSelectedRow - rowIndex),
        )
          .fill(1)
          .map(
            (_, i) =>
              i +
              Math.min(rowIndex, state.lastSelectedRow) +
              (state.lastSelectedRow > rowIndex ? 0 : 1),
          );
        keys.push(...newSelection.map((current) => makeRowKey(current)));
        newSelection.forEach((current) => {
          if (!state.shownRows.includes(current)) return;

          if (state.selectedRows.includes(current)) {
            state.selectedRows = state.selectedRows.filter(
              (search) => search !== current,
            );
          } else {
            state.selectedRows.push(current);
          }
        });
      }
    } else {
      if (state.selectedRows.includes(rowIndex)) {
        state.selectedRows = state.selectedRows.filter(
          (current) => current !== rowIndex,
        );
      } else {
        state.selectedRows.push(rowIndex);
      }
    }
  }
  state.lastSelectedRow = rowIndex;
});

const operations = {
  filter,
  /**
   // eslint-disable-next-line comment-length/limit-multi-line-comments
   * **Este método debe llamarse solamente cuando se efectúan acciones desde el
   * teclado**. Su función es controlar el flujo correcto de foco y selección de
   * acuerdo a las especificaciones de wai-aria.
   */
  moveFocusAndToggleSelection,
  resetFocus,
  /**
   * Este método debe utilizarse para la búsqueda por caracter simple o
   * múltiple en cuanto a la implementación de las funcionalidades
   * especificadas por wai-aria.
   */
  searchElementsByStartingCharacters,
  /**
   * **Método empleado cuando se presiona ctrl+shift+A**. Su función es
   * controlar el flujo correcto de foco y selección de acuerdo a las
   * especificaciones de wai-aria.
   */
  selectAll,
  /**
   * Permite alterar el estado en forma arbitraria. Debe pasarse un array con
   * los keys afectados por la acción efectuada.
   */
  setState,
  /**
   * **Este método debe llamarse solamente cuando se efectúan acciones con el
   * mouse**. Su función es controlar el flujo correcto de foco y selección de
   * acuerdo a las especificaciones de wai-aria.
   */
  updateFocusByClickEvent,
  /**
   * **Este método debe llamarse solamente cuando se efectúan acciones con la
   * tecla espacio**. Su función es controlar el flujo correcto de foco y
   * selección de acuerdo a las especificaciones de wai-aria.
   */
  updateFocusBySpaceEvent,
};
export { operations };
